import { useEffect, useMemo, useRef, useState } from "react";
import "../landingscreen/providerfilter.css";
import "./tasktypefilter.css";

/* The types offered even on a day that has none of them, in this order. */
export const TASK_TYPES = [
  "Refills",
  "Labs",
  "Lab Results",
  "Procedures",
  "Injections",
  "Notes",
  "Imaging",
  "Schedule-RTO",
];

/* orders_master.ordertype is free text: "Labs" and "labs", "procedure",
   "Procedures" and "Procedures/Tests" all occur. Keys are the value
   lowercased, non-alphanumerics dropped and one trailing "s" removed. */
const TYPE_ALIASES = {
  refill: "Refills",
  lab: "Labs",
  labresult: "Lab Results",
  procedure: "Procedures",
  procedurestest: "Procedures",
  injection: "Injections",
  note: "Notes",
  imaging: "Imaging",
  schedulerto: "Schedule-RTO",
  schedule: "Schedule-RTO",
  diagnostictest: "Diagnostic Tests",
  prescription: "Prescriptions",
};

/* The filter label an order belongs under. Types outside the alias table
   keep their own name, so a task of any type can still be picked. */
export const taskTypeOf = (order) => {
  const raw = String(order?.ordertype || "").trim();
  if (!raw) return "Other";
  const key = raw.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/s$/, "");
  return TYPE_ALIASES[key] || raw.charAt(0).toUpperCase() + raw.slice(1);
};

/* Keeps the orders whose type is selected and drops patients left with none.
   An empty selection means every type. */
export const filterTasksByType = (groups, selected) => {
  if (!selected.length) return groups;
  return groups
    .map((group) => {
      const orders = (group.orders || []).filter((o) =>
        selected.includes(taskTypeOf(o)),
      );
      return orders.length ? { ...group, orders } : null;
    })
    .filter(Boolean);
};

const CheckGlyph = () => (
  <svg fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* Task type multi-select, the dashboard's Provider filter (brainycarehome.jsx)
   with a count per type. `tasks` is the list before type filtering, so the
   counts follow the search and status but not this filter. Empty `selected`
   means every type; filtering is live and Done only dismisses. */
const TaskTypeFilter = ({ tasks, selected, setSelected }) => {
  const [open, setOpen] = useState(false);
  const controlRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (controlRef.current && !controlRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const { counts, total } = useMemo(() => {
    const counts = {};
    let total = 0;
    tasks.forEach((group) =>
      (group.orders || []).forEach((o) => {
        const type = taskTypeOf(o);
        counts[type] = (counts[type] || 0) + 1;
        total += 1;
      }),
    );
    return { counts, total };
  }, [tasks]);

  // The fixed list, then any other type on the day (Alerts, Referrals, ...)
  // and anything still selected that the day no longer has.
  const options = useMemo(() => {
    const extra = [...Object.keys(counts), ...selected].filter(
      (t, i, all) => !TASK_TYPES.includes(t) && all.indexOf(t) === i,
    );
    return [...TASK_TYPES, ...extra.sort()];
  }, [counts, selected]);

  const label =
    selected.length === 0
      ? "All"
      : selected.length === 1
        ? selected[0]
        : `${selected.length} types`;

  const toggle = (type) =>
    setSelected(
      selected.includes(type)
        ? selected.filter((t) => t !== type)
        : [...selected, type],
    );

  return (
    <div className="bmpf bmtf" ref={controlRef}>
      <button
        type="button"
        className={`provider-filter-button${selected.length ? " active" : ""}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <svg
          className="provider-filter-user"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <rect x="5.5" y="4.5" width="13" height="16" rx="2" />
          <path d="M9 4.5V3.5h6v1" />
          <path d="M9 11h6M9 15h4" />
        </svg>
        {selected.length === 0 && (
          <span className="provider-filter-caption">Type</span>
        )}
        <strong>{label}</strong>
        <svg
          className="provider-filter-chevron"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        className={`provider-filter-popover${open ? " open" : ""}`}
        aria-hidden={!open}
      >
        <div className="provider-popover-head">
          <div>
            <strong>Task types</strong>
            <span>Select one or more</span>
          </div>
          <button
            type="button"
            className="provider-popover-clear"
            onClick={() => setSelected([])}
          >
            Clear
          </button>
        </div>

        <button
          type="button"
          className={`provider-option provider-all-option${
            selected.length === 0 ? " selected" : ""
          }`}
          onClick={() => setSelected([])}
        >
          <span className="provider-option-check">
            <CheckGlyph />
          </span>
          <span className="provider-option-copy">
            <strong>All types</strong>
            <small>Show every task</small>
          </span>
          <span className="task-type-count">{total}</span>
        </button>

        <div className="provider-option-list">
          {options.map((type) => {
            const count = counts[type] || 0;
            return (
              <button
                key={type}
                type="button"
                className={`provider-option${
                  selected.includes(type) ? " selected" : ""
                }${count ? "" : " empty"}`}
                onClick={() => toggle(type)}
              >
                <span className="provider-option-check">
                  <CheckGlyph />
                </span>
                <span className="provider-option-copy">
                  <strong>{type}</strong>
                </span>
                <span className="task-type-count">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="provider-popover-foot">
          <span>
            {selected.length === 0 ? "All task types" : selected.join(", ")}
          </span>
          <button type="button" onClick={() => setOpen(false)}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskTypeFilter;
